import {useEffect, useState} from 'react'
import PropTypes from 'prop-types'
import {
  StyledSavedFilterDialogHeaderText,
  StyledSavedFiltersCancelButton,
  StyledSaveFiltersTextField,
  StyledBackIconButton,
  StyledSaveFiltersDialog,
  StyledSaveFiltersDialogActions,
  StyledSaveFiltersSaveButton,
  StyledSaveDialogTitle,
  StyledCircularProgress
} from '../FilterPanelStyle'
import DialogContent from '@mui/material/DialogContent'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import SaveFilterSnackBar from './SaveFilterSnackBar'
import {useFilterManagement} from '../../../store/hooks/useFilterManagement'
import {useSavedFilter} from '../../../store/hooks/savedFilter'
import useCreateSavedFilter from '../../../api/queries/SavedFilters/queryCreateSavedFilter'
import useUpdateSavedFilter from '../../../api/queries/SavedFilters/queryUpdateSavedFilter'

const SaveFiltersDialog = ({open, onClose, currentFilterName, data, onSuccess}) => {
  const [filterName, setFilterName] = useState(currentFilterName || '')
  const [isSnackBarOpen, setIsSnackBarOpen] = useState(false)
  const [errorMessage, setErrorMessage] = useState({show: false, message: ''})

  const {addedFilters} = useFilterManagement()
  const {
    appliedSavedFilterName,
    setCurrentAppliedSavedFilterName,
    addSavedFilter,
    updateSavedFilterName,
    savedFilterList
  } = useSavedFilter()
  const {createSavedFilter, loading} = useCreateSavedFilter()
  const {updateSavedFilter, loading: updateLoading} = useUpdateSavedFilter()

  const isLoading = loading || updateLoading
  const saveButtonText = currentFilterName ? 'Save' : 'Save and Apply'

  const SavedFilterGroupsNames = new Set(savedFilterList.map((filter) => filter.name.toLowerCase()))

  useEffect(() => {
    if (open) {
      setFilterName(currentFilterName || '')
      setErrorMessage({show: false, message: ''})
    }
  }, [open, currentFilterName])

  const handleInputChange = (e) => {
    setFilterName(e.target.value)

    if (e.target.value.length > 50) {
      setErrorMessage({show: true, message: 'Filter group name cannot exceed 50 characters.'})
    } else if (/[^A-Za-z0-9 _-]/.test(e.target.value)) {
      setErrorMessage({show: true, message: 'Special characters (such as !,@,#,$,%,&,*) are not allowed.'})
    } else if (
      SavedFilterGroupsNames.has(e.target.value.toLowerCase().trim()) &&
      e.target.value.trim().toLowerCase() !== currentFilterName.toLowerCase()
    ) {
      setErrorMessage({
        show: true,
        message: 'A filter group with this name already exists. Please choose a different name.'
      })
    } else {
      setErrorMessage({show: false, message: ''})
    }
  }

  const getFilterObject = () => {
    return {
      name: filterName,
      filters: addedFilters.map((filter) => ({
        FILTER_KEY: filter
      }))
    }
  }

  // HANDLERS FOR CREATE SAVED FILTER MUTATIONS
  const handleCreate = async () => {
    const savedFilter = getFilterObject()

    try {
      // Create saved filter via mutation
      const result = await createSavedFilter({
        variables: {
          saved_filter_name: savedFilter.name,
          saved_filter_values: savedFilter.filters
        }
      })

      // On success, update Redux store and show snackbar
      if (result?.data?.createSavedFilter?.success) {
        setCurrentAppliedSavedFilterName(filterName)
        const transformedFilters = {
          name: savedFilter.name,
          filters: savedFilter.filters.map((value) => ({
            filterKey: value.FILTER_KEY
            // Additional fields can be added here if needed
          }))
        }

        addSavedFilter(transformedFilters)
        handleSnackbarOpen()
        onClose()
      } else {
        setErrorMessage({
          show: true,
          message: result?.data?.createSavedFilter?.message || 'Failed to save filter'
        })
      }
    } catch (err) {
      console.error('Error saving filter:', err)
      setErrorMessage({
        show: true,
        message: 'Failed to save filter. Please try again.'
      })
    }
  }

  // HANDLERS FOR UPDATE SAVED FILTER MUTATIONS (EDIT NAME)
  const handleEdit = async () => {
    try {
      // Only update if filter name actually changed
      if (currentFilterName !== filterName && filterName.trim() !== '') {
        const result = await updateSavedFilter({
          variables: {
            saved_filter_name: currentFilterName,
            updated_saved_filter_name: filterName
          }
        })

        // On success, update Redux store and show snackbar
        if (result?.data?.updateSavedFilter?.success) {
          updateSavedFilterName({oldName: currentFilterName, newName: filterName})
          if (appliedSavedFilterName === currentFilterName) {
            setCurrentAppliedSavedFilterName(filterName)
          }
          handleSnackbarOpen()
          onSuccess()
          onClose()
        } else {
          setErrorMessage({
            show: true,
            message: result?.data?.updateSavedFilter?.message || 'Failed to update filter name'
          })
        }
      } else {
        // No changes made, just close
        onClose()
      }
    } catch (error) {
      console.error('Error updating filter name:', error)
      setErrorMessage({
        show: true,
        message: error?.message || 'Failed to update filter name. Please try again.'
      })
    }
  }

  // COMMON SUBMIT HANDLER
  const handleSubmit = async (e) => {
    e.preventDefault()

    if (currentFilterName !== '' || data.icon === true) {
      await handleEdit()
    } else {
      await handleCreate()
    }
  }

  // KEYBOARD NAVIGATION HANDLERS
  const handleKeyDown = (event) => {
    if (event.key === 'Enter') {
      event.preventDefault()
      if (!filterName.trim() || errorMessage.show || isLoading) {
        return // Don't submit if validation fails
      }
      handleSubmit(event)
    }
    if (event.key === 'Escape') {
      event.preventDefault()
      handleCancel()
    }
  }

  // CANCEL HANDLER
  const handleCancel = () => {
    if (isLoading) {
      return
    }

    setFilterName('')
    setErrorMessage({show: false, message: ''})
    onClose()
  }

  // SNACKBAR HANDLERS
  const handleSnackbarOpen = () => {
    setIsSnackBarOpen(true)
  }

  const handleSnackbarClose = () => {
    setIsSnackBarOpen(false)
    setFilterName('')
  }

  return (
    <>
      <StyledSaveFiltersDialog
        open={open}
        onClose={handleCancel}
        onKeyDown={handleKeyDown}
        aria-labelledby='alert-dialog-title'
        aria-describedby='alert-dialog-description'
      >
        <StyledSaveDialogTitle id='alert-dialog-title'>
          <StyledSavedFilterDialogHeaderText>
            {data.icon && (
              <span>
                <StyledBackIconButton onClick={handleCancel} aria-label='back' disabled={isLoading}>
                  <ArrowBackIcon color='primary' />
                </StyledBackIconButton>
              </span>
            )}
            {data.title}
          </StyledSavedFilterDialogHeaderText>
        </StyledSaveDialogTitle>
        <DialogContent>
          {isLoading && <StyledCircularProgress size={20} />}
          <StyledSaveFiltersTextField
            error={errorMessage.show}
            helperText={errorMessage.show ? errorMessage.message : ''}
            fullWidth
            id='filter-group-name'
            label='Filter group name'
            variant='outlined'
            value={filterName}
            placeholder='Enter filter group name'
            onChange={handleInputChange}
            disabled={isLoading}
          />
        </DialogContent>
        {!isLoading && (
          <StyledSaveFiltersDialogActions>
            <StyledSavedFiltersCancelButton onClick={handleCancel} disabled={isLoading}>
              Cancel
            </StyledSavedFiltersCancelButton>
            <StyledSaveFiltersSaveButton
              onClick={handleSubmit}
              variant='contained'
              disabled={!filterName.trim() || errorMessage.show || isLoading}
            >
              {isLoading ? 'Saving...' : saveButtonText}
            </StyledSaveFiltersSaveButton>
          </StyledSaveFiltersDialogActions>
        )}
      </StyledSaveFiltersDialog>

      <SaveFilterSnackBar
        isSnackBarOpen={isSnackBarOpen}
        handleSnackbarClose={handleSnackbarClose}
        data={
          currentFilterName !== '' || data.icon === true ? (
            <>
              Filter group label <strong>{filterName}</strong> Updated.
            </>
          ) : (
            <>
              <strong>{filterName}</strong> added to saved filter
            </>
          )
        }
        action='add'
      />
    </>
  )
}

export default SaveFiltersDialog

SaveFiltersDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  currentFilterName: PropTypes.string,
  data: PropTypes.shape({
    icon: PropTypes.bool,
    title: PropTypes.string
  }).isRequired,
  onSuccess: PropTypes.func
}
